import { useMemo, type FC } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Waypoint } from 'react-waypoint';
import ModerationAlert from '@wix/da-ds/pkg/Icons/16x16/ModerationAlert';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { STUDIO_VIDEO_GENERATIONS_STREAM } from '../../../../constants';
import VideoCreatorPlaceholder from './VideoCreatorPlaceholder';
import VideoCreatorLoading from './VideoCreatorLoading';
import VideoCreatorModeration from './VideoCreatorModeration';
import VideoFile from './VideoFile';
import {
  getVideoCreatorStatus,
  getVideoCreatorDidCreate,
  getVideoGenerationsByIds,
} from '../../../selectors/studio';

import s from './VideoCreatorFiles.scss';

export interface Props {}

const VideoCreatorFiles: FC<Props> = () => {
  const { t } = useTranslation();
  const isBusy = useSelector(getVideoCreatorStatus) === 'busy';
  const didCreate = useSelector(getVideoCreatorDidCreate);
  const showModeration = false; // TODO

  // Get video generation IDs from stream
  // Note: Stream is initialized by the saga in handleInitializeVideoCreator
  const {
    items: generationIds = [],
    hasMore,
    fetchNext,
    isFetching,
  } = useStream<number>(STUDIO_VIDEO_GENERATIONS_STREAM.streamId);

  // Get actual video generation objects from entities
  const videoGenerations = useSelector(state =>
    getVideoGenerationsByIds(state, generationIds || [])
  );

  const showPlaceholder = useMemo(() => {
    return !isBusy && !didCreate;
  }, [isBusy, didCreate]);

  return (
    <div className={s['root']}>
      <div className={s['header']}>
        <div className={s['header-title']}>
          {t('studio.videoCreator.resultsLabel')}
        </div>
        <div className={s['header-detail']}>
          <ModerationAlert className={s['alert-icon']} size={IconSize.SMALL} />
          {t('studio.videoCreator.resultsDisclaimer')}
        </div>
      </div>

      <div className={s['scroller']}>
        {isBusy && <VideoCreatorLoading />}
        {showPlaceholder && <VideoCreatorPlaceholder />}
        {showModeration && <VideoCreatorModeration companyName="TODO" />}
        {videoGenerations.length > 0 && (
          <>
            {videoGenerations.map(video => (
              <VideoFile key={video.generationId} videoGeneration={video} />
            ))}
            {hasMore && (
              <Waypoint
                onEnter={() => {
                  if (!isFetching) {
                    fetchNext();
                  }
                }}
                bottomOffset="20%"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

VideoCreatorFiles.displayName = 'VideoCreatorFiles';
export default VideoCreatorFiles;
